import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import RobotsBlockedAgents, { RobotsBlockedAgent } from '../components/RobotsBlockedAgents';
import BlockReasonPopover, { BlockReason } from '../components/BlockReasonPopover';

interface BotMeta {
  id: string;
  name: string;
  vendor: string;
  purpose: string;
  robots_token: string;
  user_agent?: string;
}

type BotStatus = 'allowed' | 'blocked' | 'partial' | 'robots_only' | 'error' | 'inconclusive';

interface BotResult {
  id: string;
  name: string;
  vendor: string;
  robots_token: string;
  robots_blocked: boolean | null;
  user_agent?: string | null;
  http_status: number | null;
  http_status_reason?: string | null;
  http_blocked: boolean;
  http_inconclusive?: boolean;
  http_final_url?: string | null;
  request_headers?: Record<string, string> | null;
  response_headers?: Record<string, string> | null;
  meta_blocked?: boolean;
  meta_reason?: string | null;
  status: BotStatus;
  evidence: string;
  error?: string | null;
  impact?: string | null;
  block_reasons?: BlockReason[] | null;
}

interface SiteResult {
  url: string;
  robots: { fetched: boolean; status: number | null; error: string | null; blocked_agents?: RobotsBlockedAgent[] };
  homepage?: {
    status_code: number | null;
    ok: boolean;
    error: string | null;
    prober_ip_blocked?: boolean;
    warp_retry_used?: boolean;
    direct_prober_blocked?: boolean;
    server?: string | null;
    meta_robots?: { name: string; content: string }[];
    x_robots_tag?: string | null;
  };
  results: BotResult[];
}

interface SnapshotClient {
  client_page_id: string;
  name: string;
  website: string | null;
  result: SiteResult | null;
  allowed: number;
  blocked: number;
  errors: number;
  total: number;
  homepage_status: string | null;
  industry?: string | null;
  is_bh?: boolean;
  checked_at: string | null;
}

interface ReportData {
  title: string | null;
  created_at: string | null;
  clients: SnapshotClient[];
  bots: BotMeta[];
  view_count: number;
}

const STATUS_STYLES: Record<BotStatus, { label: string; cls: string; dot: string }> = {
  allowed: { label: 'Allowed', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200', dot: 'bg-emerald-500' },
  blocked: { label: 'Blocked', cls: 'bg-rose-50 text-rose-700 border-rose-200', dot: 'bg-rose-500' },
  partial: { label: 'Partially blocked', cls: 'bg-orange-50 text-orange-700 border-orange-200', dot: 'bg-orange-500' },
  robots_only: { label: 'Robots OK', cls: 'bg-sky-50 text-sky-700 border-sky-200', dot: 'bg-sky-500' },
  error: { label: 'Unreachable', cls: 'bg-amber-50 text-amber-700 border-amber-200', dot: 'bg-amber-500' },
  inconclusive: { label: 'Inconclusive', cls: 'bg-amber-50 text-amber-700 border-amber-200', dot: 'bg-amber-500' },
};

function fmtDate(iso: string | null): string {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

export default function LLMBlockTestReportPage() {
  const { shareToken } = useParams<{ shareToken: string }>();
  const [data, setData] = useState<ReportData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [expandedBot, setExpandedBot] = useState<Record<string, boolean>>({});

  const toggleBot = (clientId: string, botId: string) => {
    const k = `${clientId}::${botId}`;
    setExpandedBot((prev) => ({ ...prev, [k]: !prev[k] }));
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const r = await fetch(`/api/llm-block-test/public/report/${shareToken}`);
        if (!r.ok) {
          throw new Error(r.status === 404 ? 'Report not found or revoked.' : `HTTP ${r.status}`);
        }
        const json = await r.json();
        if (!cancelled) setData(json);
      } catch (e: any) {
        if (!cancelled) setError(e?.message || 'Failed to load report');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [shareToken]);

  const summary = useMemo(() => {
    if (!data) return null;
    const total = data.clients.length;
    const fullyAllowed = data.clients.filter((c) => c.total > 0 && c.allowed === c.total).length;
    const blocking = data.clients.filter((c) => c.blocked > 0).length;
    const errored = data.clients.filter((c) => c.errors > 0 && c.blocked === 0).length;
    const totalBots = data.clients.reduce((sum, c) => sum + (c.total || 0), 0);
    const totalBlocked = data.clients.reduce((sum, c) => sum + (c.blocked || 0), 0);
    return { total, fullyAllowed, blocking, errored, totalBots, totalBlocked };
  }, [data]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white">
        <div className="text-sm opacity-70">Loading report…</div>
      </div>
    );
  }
  if (error || !data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white">
        <div className="text-center">
          <div className="text-2xl font-bold mb-2">Report unavailable</div>
          <div className="text-sm opacity-70">{error || 'Unknown error'}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-50">
      {/* Hero */}
      <header className="bg-gradient-to-br from-slate-900 via-slate-800 to-indigo-900 text-white">
        <div className="max-w-6xl mx-auto px-6 py-12">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-brand-400 to-brand-600 flex items-center justify-center">
              <span className="text-white font-bold text-lg">BN</span>
            </div>
            <div className="text-xs uppercase tracking-widest opacity-70">BrandNorth · LLM Block Test</div>
          </div>
          <h1 className="text-4xl font-bold mb-3">
            {data.title || 'AI crawler block report'}
          </h1>
          <p className="text-sm opacity-80 max-w-2xl">
            How each website behaves when the major AI crawlers (OpenAI, Anthropic,
            Perplexity, Google Gemini, Apple, Meta) try to access it. Each site is
            checked at three layers: robots.txt, homepage HTML/headers, and a live
            HTTP request with the bot's User-Agent.
          </p>
          <div className="mt-6 flex flex-wrap gap-x-8 gap-y-2 text-sm">
            {data.created_at && (
              <div>
                <div className="text-xs uppercase opacity-60">Generated</div>
                <div className="font-medium">{fmtDate(data.created_at)}</div>
              </div>
            )}
            <div>
              <div className="text-xs uppercase opacity-60">Sites in report</div>
              <div className="font-medium">{summary?.total ?? 0}</div>
            </div>
            <div>
              <div className="text-xs uppercase opacity-60">AI bots tested per site</div>
              <div className="font-medium">{data.bots.length || 10}</div>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-10 space-y-10">
        {/* Summary cards */}
        {summary && (
          <section className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <SummaryCard label="Sites fully open" value={summary.fullyAllowed}
                         total={summary.total} tone="emerald" />
            <SummaryCard label="Sites blocking ≥1 bot" value={summary.blocking}
                         total={summary.total} tone="rose" />
            <SummaryCard label="Sites with errors" value={summary.errored}
                         total={summary.total} tone="amber" />
            <SummaryCard label="Total bot blocks" value={summary.totalBlocked}
                         total={summary.totalBots} tone="slate" />
          </section>
        )}

        {/* Bot legend */}
        <section>
          <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
            Bots covered in this report
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
            {data.bots.map((b) => (
              <div key={b.id} className="text-xs bg-white border border-gray-200 rounded-md px-3 py-2">
                <div className="font-medium text-gray-900">{b.name}</div>
                <div className="text-gray-500">{b.vendor}</div>
                <div className="text-gray-400 mt-1 font-mono">{b.robots_token}</div>
              </div>
            ))}
          </div>
        </section>

        {/* Per-client cards */}
        <section className="space-y-4">
          <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
            Per-site results
          </h2>
          {data.clients.map((c, idx) => {
            const isOpen = !!expanded[c.client_page_id];
            const prev = idx > 0 ? data.clients[idx - 1] : null;
            const showBhHeader = idx === 0 && c.is_bh;
            const showOtherHeader = !c.is_bh && (idx === 0 || prev?.is_bh);
            const bhCount = data.clients.filter((x) => x.is_bh).length;
            const otherCount = data.clients.length - bhCount;
            const r = c.result;
            const homepageBadge = (() => {
              if (!r?.homepage) return null;
              if (r.homepage.ok) {
                return <Badge tone="emerald">{r.homepage.status_code} OK</Badge>;
              }
              if (r.homepage.error) {
                return <Badge tone="amber" title={r.homepage.error}>unreachable</Badge>;
              }
              return <Badge tone="rose">HTTP {r.homepage.status_code ?? '?'}</Badge>;
            })();
            return (
              <React.Fragment key={c.client_page_id}>
              {showBhHeader && (
                <div className="flex items-center gap-3 pt-2 pb-1">
                  <div className="h-px flex-1 bg-emerald-200" />
                  <span className="text-xs font-semibold uppercase tracking-wider text-emerald-700">
                    Behavioral Health · {bhCount}
                  </span>
                  <div className="h-px flex-1 bg-emerald-200" />
                </div>
              )}
              {showOtherHeader && (
                <div className="flex items-center gap-3 pt-4 pb-1">
                  <div className="h-px flex-1 bg-slate-300" />
                  <span className="text-xs font-semibold uppercase tracking-wider text-slate-600">
                    Other industries · {otherCount}
                  </span>
                  <div className="h-px flex-1 bg-slate-300" />
                </div>
              )}
              <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                <button
                  onClick={() => setExpanded({ ...expanded, [c.client_page_id]: !isOpen })}
                  className="w-full flex items-center gap-4 px-5 py-4 text-left hover:bg-gray-50"
                >
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-gray-900 truncate">{c.name}</div>
                    {c.website && (
                      <div className="text-xs text-gray-500 truncate font-mono">{c.website}</div>
                    )}
                  </div>
                  <div className="hidden sm:flex items-center gap-3 text-xs">
                    {homepageBadge}
                    <Pill label="Allowed" value={c.allowed} total={c.total} tone="emerald" />
                    <Pill label="Blocked" value={c.blocked} total={c.total}
                          tone={c.blocked > 0 ? 'rose' : 'slate'} />
                    {c.errors > 0 && (
                      <Pill label="Errors" value={c.errors} total={c.total} tone="amber" />
                    )}
                    {!!r?.robots.blocked_agents?.length && (
                      <Badge tone="rose" title={r.robots.blocked_agents.map((a) => a.assistant ? `${a.agent} (${a.assistant})` : a.agent).join(', ')}>
                        robots.txt: {r.robots.blocked_agents.some((a) => a.all_bots)
                          ? 'all bots blocked'
                          : `${r.robots.blocked_agents.length} AI crawler${r.robots.blocked_agents.length === 1 ? '' : 's'} blocked`}
                      </Badge>
                    )}
                  </div>
                  <svg className={`w-5 h-5 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
                       fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {isOpen && r && (
                  <div className="border-t border-gray-100 p-5 bg-gray-50/40">
                    {r.homepage?.prober_ip_blocked && (
                      <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded text-xs text-amber-900">
                        <div className="font-semibold mb-1">
                          HTTP probe inconclusive — site returned HTTP {r.homepage.status_code} to a normal browser too
                          {r.homepage.server?.includes('cloudflare') ? ' (Cloudflare)' : ''}.
                        </div>
                        <div className="opacity-80">
                          The site is blocking our prober's IP at the WAF/CDN layer, so we can't isolate UA-level
                          bot blocking. Verdict below relies on robots.txt + homepage meta signals only.
                        </div>
                      </div>
                    )}
                    <RobotsBlockedAgents agents={r.robots.blocked_agents} />
                    {(r.homepage?.meta_robots && r.homepage.meta_robots.length > 0) || r.homepage?.x_robots_tag ? (
                      <div className="mb-4 p-3 bg-white border border-amber-200 rounded text-xs text-gray-700 space-y-1">
                        <div className="font-semibold text-amber-700">Homepage AI signals:</div>
                        {r.homepage?.meta_robots?.map((mr, i) => (
                          <div key={i} className="font-mono">&lt;meta name="{mr.name}" content="{mr.content}"&gt;</div>
                        ))}
                        {r.homepage?.x_robots_tag && (
                          <div className="font-mono">X-Robots-Tag: {r.homepage.x_robots_tag}</div>
                        )}
                      </div>
                    ) : null}
                    <div className="overflow-x-auto">
                      <table className="w-full text-xs">
                        <thead className="bg-white border-b border-gray-200">
                          <tr>
                            <th className="text-left px-3 py-2 font-medium text-gray-600">Bot</th>
                            <th className="text-left px-3 py-2 font-medium text-gray-600">Vendor</th>
                            <th className="text-left px-3 py-2 font-medium text-gray-600">Robots</th>
                            <th className="text-left px-3 py-2 font-medium text-gray-600">Meta</th>
                            <th className="text-left px-3 py-2 font-medium text-gray-600">HTTP</th>
                            <th className="text-left px-3 py-2 font-medium text-gray-600">Status</th>
                            <th className="text-left px-3 py-2 font-medium text-gray-600">Evidence</th>
                          </tr>
                        </thead>
                        <tbody>
                          {r.results.map((br) => {
                            const s = STATUS_STYLES[br.status];
                            const k = `${c.client_page_id}::${br.id}`;
                            const isOpen = !!expandedBot[k];
                            return (
                              <React.Fragment key={br.id}>
                                <tr onClick={() => toggleBot(c.client_page_id, br.id)}
                                    className="border-b border-gray-100 bg-white hover:bg-gray-50 cursor-pointer">
                                  <td className="px-3 py-2 font-medium text-gray-900">
                                    <div className="flex items-center gap-1.5">
                                      <svg className={`w-3 h-3 text-gray-400 transition-transform ${isOpen ? 'rotate-90' : ''}`}
                                           fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                      </svg>
                                      {br.name}
                                    </div>
                                  </td>
                                  <td className="px-3 py-2 text-gray-600">{br.vendor}</td>
                                  <td className="px-3 py-2">
                                    {br.robots_blocked === true ? <span className="text-rose-700">disallow</span>
                                      : br.robots_blocked === false ? <span className="text-emerald-700">allow</span>
                                        : <span className="text-gray-400">unknown</span>}
                                  </td>
                                  <td className="px-3 py-2">
                                    {br.meta_blocked
                                      ? <span className="text-rose-700" title={br.meta_reason || ''}>opt-out</span>
                                      : <span className="text-emerald-700">none</span>}
                                  </td>
                                  <td className="px-3 py-2">
                                    {br.http_inconclusive
                                      ? <span className="text-amber-700" title="Same response as a normal browser — prober IP is blocked at the WAF/CDN layer, can't isolate UA-based blocking">{br.http_status ?? '?'} <span className="opacity-60">(?)</span></span>
                                      : br.http_status ? `${br.http_status}${br.http_blocked ? ' ✕' : ''}`
                                        : (br.error ? <span className="text-amber-700">err</span>
                                                     : <span className="text-gray-400">—</span>)}
                                  </td>
                                  <td className="px-3 py-2">
                                    <BlockReasonPopover impact={br.impact} reasons={br.block_reasons}>
                                      <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border text-[11px] font-medium ${s.cls}`}>
                                        <span className={`w-1.5 h-1.5 rounded-full ${s.dot}`} />
                                        {s.label}
                                        {!!br.block_reasons?.length && <span className="opacity-60">ⓘ</span>}
                                      </span>
                                    </BlockReasonPopover>
                                  </td>
                                  <td className="px-3 py-2 text-gray-500">{br.evidence}</td>
                                </tr>
                                {isOpen && (
                                  <tr className="bg-slate-50">
                                    <td colSpan={7} className="px-3 py-3">
                                      <BotInspector br={br} />
                                    </td>
                                  </tr>
                                )}
                              </React.Fragment>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                    {c.checked_at && (
                      <div className="text-xs text-gray-400 mt-3">Last checked: {fmtDate(c.checked_at)}</div>
                    )}
                  </div>
                )}
              </div>
              </React.Fragment>
            );
          })}
        </section>

        <footer className="text-center text-xs text-gray-400 py-8 border-t border-gray-200">
          Generated by BrandNorth · LLM Block Test
        </footer>
      </main>
    </div>
  );
}

function SummaryCard({ label, value, total, tone }: {
  label: string; value: number; total: number;
  tone: 'emerald' | 'rose' | 'amber' | 'slate';
}) {
  const cls = {
    emerald: 'text-emerald-700',
    rose: 'text-rose-700',
    amber: 'text-amber-700',
    slate: 'text-slate-800',
  }[tone];
  return (
    <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
      <div className="text-xs uppercase tracking-wider text-gray-500">{label}</div>
      <div className={`text-3xl font-bold mt-1 ${cls}`}>{value}</div>
      <div className="text-xs text-gray-400 mt-1">of {total}</div>
    </div>
  );
}

function Badge({ tone, children, title }: {
  tone: 'emerald' | 'rose' | 'amber' | 'slate';
  children: React.ReactNode;
  title?: string;
}) {
  const cls = {
    emerald: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    rose: 'bg-rose-50 text-rose-700 border-rose-200',
    amber: 'bg-amber-50 text-amber-700 border-amber-200',
    slate: 'bg-slate-50 text-slate-700 border-slate-200',
  }[tone];
  return (
    <span title={title} className={`px-2 py-0.5 rounded-full border text-[11px] font-medium ${cls}`}>
      {children}
    </span>
  );
}

function BotInspector({ br }: { br: BotResult }) {
  const reqHeaders = br.request_headers || {};
  const respHeaders = br.response_headers || {};
  const ua = br.user_agent || reqHeaders['User-Agent'] || reqHeaders['user-agent'] || '';
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
      <div className="bg-white border border-gray-200 rounded p-3">
        <div className="text-[11px] uppercase tracking-wider text-gray-500 mb-2">Request sent to website</div>
        <div className="text-xs space-y-1.5">
          <div>
            <span className="text-gray-500">Method:</span>{' '}
            <span className="font-mono">GET</span>
          </div>
          {ua && (
            <div>
              <span className="text-gray-500">User-Agent:</span>
              <div className="font-mono text-[11px] mt-0.5 p-1.5 bg-gray-50 border border-gray-100 rounded break-all">
                {ua}
              </div>
            </div>
          )}
          <div>
            <span className="text-gray-500">robots.txt token:</span>{' '}
            <span className="font-mono">{br.robots_token}</span>
          </div>
          {Object.keys(reqHeaders).length > 0 && (
            <details className="mt-1">
              <summary className="cursor-pointer text-gray-500 hover:text-gray-700">All request headers</summary>
              <pre className="font-mono text-[11px] mt-1 p-2 bg-gray-50 border border-gray-100 rounded overflow-x-auto">
{Object.entries(reqHeaders).map(([k, v]) => `${k}: ${v}`).join('\n')}
              </pre>
            </details>
          )}
        </div>
      </div>
      <div className="bg-white border border-gray-200 rounded p-3">
        <div className="text-[11px] uppercase tracking-wider text-gray-500 mb-2">Response from website</div>
        <div className="text-xs space-y-1.5">
          {br.error ? (
            <div className="bg-amber-50 border border-amber-200 rounded p-2 text-amber-800 font-mono text-[11px]">
              {br.error}
            </div>
          ) : (
            <>
              <div>
                <span className="text-gray-500">Status:</span>{' '}
                <span className={`font-mono font-semibold ${
                  br.http_blocked ? 'text-rose-700' :
                  br.http_status && br.http_status >= 200 && br.http_status < 300 ? 'text-emerald-700' :
                  'text-gray-700'
                }`}>
                  {br.http_status ?? '—'} {br.http_status_reason || ''}
                </span>
              </div>
              {br.http_final_url && (
                <div className="break-all">
                  <span className="text-gray-500">Final URL:</span>{' '}
                  <span className="font-mono">{br.http_final_url}</span>
                </div>
              )}
              {Object.keys(respHeaders).length > 0 && (
                <details className="mt-1" open>
                  <summary className="cursor-pointer text-gray-500 hover:text-gray-700">Response headers</summary>
                  <pre className="font-mono text-[11px] mt-1 p-2 bg-gray-50 border border-gray-100 rounded overflow-x-auto max-h-64">
{Object.entries(respHeaders).map(([k, v]) => `${k}: ${v}`).join('\n')}
                  </pre>
                </details>
              )}
            </>
          )}
          {br.meta_blocked && br.meta_reason && (
            <div className="mt-2 p-2 bg-amber-50 border border-amber-200 rounded">
              <div className="text-amber-800 font-semibold text-[11px] uppercase tracking-wider mb-1">
                Homepage AI opt-out signal
              </div>
              <div className="font-mono text-[11px] break-all">{br.meta_reason}</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Pill({ label, value, total, tone }: {
  label: string; value: number; total: number;
  tone: 'emerald' | 'rose' | 'amber' | 'slate';
}) {
  const cls = {
    emerald: 'text-emerald-700',
    rose: 'text-rose-700',
    amber: 'text-amber-700',
    slate: 'text-gray-600',
  }[tone];
  return (
    <span className="text-xs">
      <span className="text-gray-400 mr-1">{label}</span>
      <span className={`font-semibold ${cls}`}>{value}/{total}</span>
    </span>
  );
}
